import { useEffect, useState } from "react";
import { CalendarDays, Images, Mail, Package } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { SectionHeading } from "@/components/Brand";
import SmartImage from "@/components/SmartImage";

const CONTACT_EMAIL = "hrcreations90@gmail.com";

const FORMAT_KEYS = {
  print: "collection.formatPrint",
  digital: "collection.formatDigital",
  both: "collection.formatBoth",
};

export default function CollectionAlbums() {
  const { t, localized, lang } = useI18n();
  const [albums, setAlbums] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/collection-albums")
      .then((r) => setAlbums(r.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const mailto = (album) => {
    const title = localized(album, "title");
    const subject = lang === "en" ? `Album request: ${title}` : `Solicitud de álbum: ${title}`;
    const body =
      lang === "en"
        ? `Hello HR Creations,\n\nI am interested in the album "${title}"${album.year ? ` (${album.year})` : ""}.\nPlease send me the details and how to pay.\n\nName:\nPhone / WhatsApp:\n`
        : `Hola HR Creations,\n\nMe interesa el álbum "${title}"${album.year ? ` (${album.year})` : ""}.\nFavor de enviarme los detalles y la forma de pago.\n\nNombre:\nTeléfono / WhatsApp:\n`;
    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-14 sm:px-8 lg:px-12 lg:py-20" data-testid="collection-albums-page">
      <SectionHeading
        label={t("brand.positioning")}
        title={t("collection.title")}
        sub={t("collection.sub")}
      />

      {!loaded ? (
        <p className="mt-12 text-sm text-[#5A5752]" data-testid="collection-loading">
          {t("common.loading")}
        </p>
      ) : albums.length === 0 ? (
        <p className="mt-12 text-sm text-[#5A5752]" data-testid="collection-empty">
          {t("collection.empty")}
        </p>
      ) : (
        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="collection-grid">
          {albums.map((album) => (
            <article
              key={album.id}
              data-testid={`collection-card-${album.id}`}
              className="flex flex-col border border-[#C69A3B]/40 bg-[#FAFAF7]"
            >
              <div className="photo-hover overflow-hidden bg-[#EFEAE0]">
                {album.cover_image ? (
                  <SmartImage
                    src={album.cover_image}
                    alt={localized(album, "title")}
                    loading="lazy"
                    className="aspect-[4/3] w-full"
                  />
                ) : (
                  <div className="grid h-24 w-full place-items-center">
                    <Package size={22} className="text-[#C69A3B]" />
                  </div>
                )}
              </div>
              <div className="flex flex-1 flex-col p-6">
                <p className="label-mono">{localized(album, "theme") || t("collection.title")}</p>
                <h3 className="mt-3 font-serif-display text-2xl text-[#8F3329]">
                  {localized(album, "title")}
                </h3>
                <div className="mt-4 space-y-2 text-sm text-[#5A5752]">
                  {album.year && (
                    <p className="flex items-center gap-2">
                      <CalendarDays size={14} className="text-[#C69A3B]" /> {album.year}
                    </p>
                  )}
                  {Boolean(album.photo_count) && (
                    <p className="flex items-center gap-2">
                      <Images size={14} className="text-[#C69A3B]" /> {album.photo_count}{" "}
                      {t("events.photos")}
                    </p>
                  )}
                  <p className="flex items-center gap-2">
                    <Package size={14} className="text-[#C69A3B]" />{" "}
                    {t(FORMAT_KEYS[album.format] || FORMAT_KEYS.print)}
                  </p>
                </div>
                <p className="mt-5 font-serif-display text-3xl text-[#252525]" data-testid={`collection-price-${album.id}`}>
                  ${Number(album.price || 0).toFixed(2)}
                </p>
                {album.notes_es && (
                  <p className="mt-3 text-xs text-[#5A5752]">{localized(album, "notes")}</p>
                )}
                <a
                  href={mailto(album)}
                  data-testid={`collection-request-${album.id}`}
                  className="mt-6 inline-flex min-h-[48px] items-center justify-center gap-2 border border-[#8F3329] bg-[#8F3329] px-5 text-sm text-[#FAFAF7] transition-colors duration-300 hover:bg-[#252525]"
                >
                  <Mail size={15} /> {t("collection.request")}
                </a>
              </div>
            </article>
          ))}
        </div>
      )}

      <p className="mt-12 max-w-2xl text-sm text-[#5A5752]" data-testid="collection-note">
        {t("collection.note")} <span className="text-[#8F3329]">{CONTACT_EMAIL}</span>
      </p>
    </div>
  );
}
